import type { ReactElement } from 'react';
import {
  useEffect,
  useMemo,
  Suspense,
} from 'react';

import { Secure, useAuth } from '@virtuslab/react-oauth2';
import isNil from 'lodash/isNil';
import partition from 'lodash/partition';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  matchPath,
  useLocation,
} from 'react-router';

import Box from '@mui/material/Box';
import { QuestionIcon } from '@phosphor-icons/react';

import Body from '@virtuslab/nfs-shared/src/components/molecules/Body';
import CompanyAvatar from '@virtuslab/nfs-shared/src/components/molecules/CompanyAvatar';
import Icon from '@virtuslab/nfs-shared/src/components/molecules/Icon';
import Loading from '@virtuslab/nfs-shared/src/components/molecules/Loading';
import LoadingErrorHandler from '@virtuslab/nfs-shared/src/components/molecules/LoadingErrorHandler';
import TextButton from '@virtuslab/nfs-shared/src/components/molecules/TextButton';
import TextGroup from '@virtuslab/nfs-shared/src/components/molecules/TextGroup';
import ContentErrorBoundary from '@virtuslab/nfs-shared/src/components/organisms/ContentErrorBoundary';
import NavSidebar from '@virtuslab/nfs-shared/src/components/organisms/NavSidebar';
import ToastsContainer from '@virtuslab/nfs-shared/src/components/organisms/ToastsContainer';
import UserInContextProvider from '@virtuslab/nfs-shared/src/components/organisms/UserInContextProvider';
import { NavItem } from '@virtuslab/nfs-shared/src/config/navigation';
import type { SecureRoutesWrapperContractsQuery } from '@virtuslab/nfs-shared/src/schema/user';
import {
  useSecureRoutesWrapperContractsQuery, ContractStatus,
} from '@virtuslab/nfs-shared/src/schema/user';
import type { NonError } from '@virtuslab/nfs-shared/src/services/errors';
import { splitError } from '@virtuslab/nfs-shared/src/services/errors';
import { generatePath } from '@virtuslab/nfs-shared/src/services/routes';
import { removeInacessibleNavigationOptions } from '@virtuslab/nfs-shared/src/services/security';
import { safeWindowOpen } from '@virtuslab/nfs-shared/src/services/url';
import palette from '@virtuslab/nfs-shared/src/theme/palette';

import config from '../../../config';
import { contractParamName, nfsFeedbackSlackURL, trGuideURL } from '../../../config/component';
import navigation, { profileNavigation } from '../../../config/navigation';
import { BaseRoutes, LoginRoutes, TimeReportRoutes } from '../../../config/paths';
import type { ContextValue } from '../../../services/hooks/useContract';
import { context } from '../../../services/hooks/useContract';
import useLastContractIndex from '../../../services/hooks/useLastContractIndex';

import GraphqlProvider from '../GraphqlProvider';
import ProfileCompletionGuard from '../ProfileCompletionGuard';
import UserProfileLink from '../UserProfileLink';

export type Contract = NonError<SecureRoutesWrapperContractsQuery['profile']>['contracts'][number];

const sortContracts = (
  contractA: Contract, contractB: Contract,
) => contractA.createdAt.getTime() - contractB.createdAt.getTime();

const { search } = window.location;
const { redirectUri = undefined } = qs.parse(search, { ignoreQueryPrefix: true });
const relativeLocation = redirectUri ?? window.location.href.replace(`${config.baseUrl}`, '');
const redirectUrl = `${LoginRoutes.LOGIN}?redirectUri=${String(relativeLocation)}`;

const withProviders = (Component: () => ReactElement) => () => {
  const navigate = useNavigate();

  return (
    <Secure otherwise={<Navigate to={redirectUrl} />}>
      <ContentErrorBoundary>
        <GraphqlProvider onUnauthorized={() => navigate(redirectUrl)}>
          <Component />
        </GraphqlProvider>
      </ContentErrorBoundary>
    </Secure>
  );
};

const SecureRoutesWrapper = (): ReactElement => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const isInRoot = !isNil(matchPath(BaseRoutes.BASE, location.pathname))
   || !isNil(matchPath(BaseRoutes.CONTRACT_ROUTE, location.pathname));

  const auth = useAuth();
  const profile = auth.getUserProfile();

  const parsedProfile = useMemo(() => ({
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    id: profile.id ?? '',
    username: profile.username ?? '',
  }), [profile]);

  const params = useParams<{ [key in typeof contractParamName]: string }>();
  const { data, loading, refetch } = useSecureRoutesWrapperContractsQuery();

  const [contractsResponse, error] = splitError(data?.profile);
  const contracts = contractsResponse?.contracts;

  const sortedContracts = useMemo(() => {
    const [activeContracts, inactiveContracts] = partition(
      contracts ?? [],
      (contract) => contract.status === ContractStatus.CONCLUDED || contract.status === ContractStatus.ACTIVE,
    );

    return [
      ...activeContracts.sort(sortContracts),
      ...inactiveContracts.sort(sortContracts),
    ];
  }, [contracts]);

  const contractIndex = String(useLastContractIndex({
    contracts: sortedContracts,
    urlContractIndex: params.contractIndex,
  }));

  const contextValue = useMemo<ContextValue>(() => ({
    contracts: sortedContracts,
  }), [sortedContracts]);

  // Depends on `loading` to avoid premature navigation to /c/0 before contracts are fetched
  useEffect(() => {
    if (isInRoot && !loading) {
      navigate(generatePath(TimeReportRoutes.TIME_REPORT, {
        contractIndex,
      }));
    }
  }, [isInRoot, loading, navigate, contractIndex]);

  if (loading) {
    return <Loading />;
  }

  return (
    <context.Provider value={contextValue}>
      <UserInContextProvider userInContext={parsedProfile}>
        <ToastsContainer />
        <ProfileCompletionGuard />
        <NavSidebar
          navigation={
            removeInacessibleNavigationOptions(
              [],
              navigation(
                t,
                (url) => generatePath(url, { contractIndex }),
                sortedContracts.length <= 1 ? undefined : (root) => sortedContracts
                  .map((contract, i): NavItem => ({
                    id: `${root}-${contract.id}` ,
                    title: contract.name,
                    url: generatePath(root, { contractIndex: i }),
                    canBeAccessed: true,
                    muted: contract.status === ContractStatus.TERMINATED,
                    associatedRoutes: [`${generatePath(root, { contractIndex :i })}/*`],
                    caption: (
                      <Box display="flex" alignItems="center" gap={200}>
                        <CompanyAvatar
                          variant="xs"
                          company={contract.promisor}
                        />
                        <TextGroup hideOverflow>
                          <Body size={200} variant="secondary">
                            {contract.promisor.name}
                          </Body>
                        </TextGroup>

                      </Box>
                    )
                  }))
              ),
            )
          }
          profileLink={<UserProfileLink navigation={profileNavigation(t)} />}
        >
          <Box p={600} display="flex" flexDirection="column" gap={300}>
            <Icon size="lg">
              <QuestionIcon weight="fill" color={palette.support.info[60]} />
            </Icon>
            <TextButton size={200} onClick={() => safeWindowOpen(trGuideURL)} variant="primary">
              {t('NFS Guide')}
            </TextButton>
            <TextButton size={200} onClick={() => safeWindowOpen(nfsFeedbackSlackURL)} variant="primary">
              {t('NFS feedback (Slack)')}
            </TextButton>
          </Box>
        </NavSidebar>
        <Suspense fallback={<Loading />}>
          <LoadingErrorHandler
            error={error}
            reloadData={async () => refetch()}
            size="lg"
            forceDisplayErrorMessage={isNil(data)}
          >
            <Outlet />
          </LoadingErrorHandler>
        </Suspense>
      </UserInContextProvider>

    </context.Provider>
  );
};

export default withProviders(SecureRoutesWrapper);
